import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import ImagePicker from "@/components/ImagePicker";
import SoundPicker from "@/components/SoundPicker";
import { Image, Plus, Trash2, Play, Save, ExternalLink, Link2, Loader2, CheckCircle2, Unplug } from "lucide-react";
import { toast } from "sonner";

const newRedeem = () => ({
  id: `r_${Math.random().toString(36).slice(2, 10)}`,
  name: "", reward_id: "", image_url: "", sound_url: "", duration: 8,
  position: "center", size: "large", cooldown: 0, cost: 100, is_enabled: true,
});

const POSITIONS = [
  { v: "center", l: "Center" },
  { v: "top", l: "Top" },
  { v: "bottom", l: "Bottom" },
  { v: "top-left", l: "Top left" },
  { v: "top-right", l: "Top right" },
  { v: "bottom-left", l: "Bottom left" },
  { v: "bottom-right", l: "Bottom right" },
];
const SIZES = [
  { v: "small", l: "Small" },
  { v: "medium", l: "Medium" },
  { v: "large", l: "Large" },
  { v: "full", l: "Full screen" },
];

export default function MediaRedeems() {
  const [items, setItems] = useState([]);
  const [lastRedeem, setLastRedeem] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [bcStatus, setBcStatus] = useState(null);
  const [bcBusy, setBcBusy] = useState(false);
  const [creatingId, setCreatingId] = useState(null);

  const loadBc = async () => { try { setBcStatus((await api.get("/twitch/broadcaster/status")).data); } catch (e) {} };
  const load = async () => {
    try { setItems((await api.get("/media/redeems")).data); } catch (e) {}
    try { setLastRedeem((await api.get("/config/chat")).data.last_redeem_id || ""); } catch (e) {}
  };
  useEffect(() => {
    load(); loadBc();
    const params = new URLSearchParams(window.location.search);
    const bc = params.get("bc");
    if (bc === "ok") toast.success("Twitch channel connected — you can create rewards now");
    else if (bc) toast.error(`Twitch connect failed: ${bc.replace(/_/g, " ")}`, { duration: 8000 });
    const t = setInterval(async () => {
      try { setLastRedeem((await api.get("/config/chat")).data.last_redeem_id || ""); } catch (e) {}
    }, 3000); return () => clearInterval(t);
  }, []);

  const connectBroadcaster = async () => {
    setBcBusy(true);
    try { const r = await api.post("/twitch/broadcaster/start"); window.location.href = r.data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Twitch connect"); setBcBusy(false); }
  };
  const disconnectBroadcaster = async () => {
    setBcBusy(true);
    try { await api.post("/twitch/broadcaster/disconnect"); await loadBc(); toast.success("Twitch channel disconnected"); }
    catch (e) { toast.error("Disconnect failed"); } finally { setBcBusy(false); }
  };
  const createReward = async (r) => {
    if (!bcStatus?.connected) { toast.error("Connect your Twitch channel first"); return; }
    if (!(r.name || "").trim()) { toast.error("Give the redeem a name first"); return; }
    setCreatingId(r.id);
    try {
      const res = await api.post("/twitch/rewards/create", {
        title: r.name, cost: parseInt(r.cost) || 100,
      });
      const next = items.map((x) => x.id === r.id ? { ...x, reward_id: res.data.reward_id, is_enabled: res.data.is_enabled } : x);
      const saved = await api.post("/media/redeems", { items: next });
      setItems(saved.data); setDirty(false);
      toast.success(`Reward "${res.data.title}" created on Twitch`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the reward"); }
    finally { setCreatingId(null); }
  };
  const toggleReward = async (r, val) => {
    if (!r.reward_id) { patch(r.id, "is_enabled", val); return; }
    try {
      await api.patch(`/twitch/rewards/${r.reward_id}`, { is_enabled: val });
      setItems((prev) => prev.map((x) => x.id === r.id ? { ...x, is_enabled: val } : x)); setDirty(true);
    } catch (e) { toast.error("Could not update the reward on Twitch"); }
  };

  const patch = (id, key, val) => {
    setItems((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: val } : r)));
    setDirty(true);
  };
  const add = () => { setItems((prev) => [...prev, newRedeem()]); setDirty(true); };
  const remove = async (id) => {
    const row = items.find((r) => r.id === id);
    if (row?.reward_id) { try { await api.delete(`/twitch/rewards/${row.reward_id}`); } catch (e) {} }
    setItems((prev) => prev.filter((r) => r.id !== id)); setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post("/media/redeems", { items });
      setItems(r.data);
      setDirty(false);
      toast.success("Media redeems saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const test = async (id) => {
    try {
      if (dirty) {
        const r = await api.post("/media/redeems", { items });
        setItems(r.data);
        setDirty(false);
      }
      await api.post("/media/redeems/test", { id });
      toast.success("Triggered! Open the overlay to see it 🎉");
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="media-redeems-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-800 font-heading">Media Redeems</h1>
          <p className="text-muted-foreground mt-1 text-sm">Map a Twitch channel-point reward to a picture + sound. When a viewer redeems it, the image pops up on your overlay and the sound plays. Create the reward right here — no need to make it on Twitch first.</p>
        </div>
        <Button onClick={save} disabled={saving || !dirty} className="bg-primary hover:bg-primary/90 rounded-lg shrink-0" data-testid="save-redeems-button">
          <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save"}
        </Button>
      </header>

      {/* Broadcaster connection */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg p-3 border border-white/10 bg-[#0a0a0a]" data-testid="media-broadcaster-box">
        {bcStatus?.connected ? (
          <>
            <span className="inline-flex items-center gap-1.5 text-xs text-green-400"><CheckCircle2 className="w-4 h-4" />Twitch connected as <b className="text-white">{bcStatus.display_name || bcStatus.login}</b> — you can create rewards below.</span>
            <Button variant="outline" size="sm" className="border-white/10 h-8 ml-auto" onClick={disconnectBroadcaster} disabled={bcBusy} data-testid="media-broadcaster-disconnect"><Unplug className="w-4 h-4 mr-1" />Disconnect</Button>
          </>
        ) : (
          <>
            <span className="text-xs text-muted-foreground">Connect your channel once so the app can create channel-point rewards for you (like Blerp).</span>
            <Button size="sm" className="bg-[#9146FF] hover:bg-[#9146FF]/90 h-8 ml-auto" onClick={connectBroadcaster} disabled={bcBusy || !bcStatus} data-testid="media-broadcaster-connect"><Link2 className="w-4 h-4 mr-1" />Connect Twitch as Broadcaster</Button>
          </>
        )}
      </div>

      <div className="rounded-lg p-4 border border-white/10 bg-[#0a0a0a] text-xs text-muted-foreground">
        Open the <a href="/overlay" target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5">overlay <ExternalLink className="w-3 h-3" /></a> as a browser source in OBS so redeemed media shows on stream.
      </div>

      <div className="space-y-4">
        {items.map((r, i) => (
          <div key={r.id} className="bg-[#0a0a0a] border border-white/10 rounded-lg p-5 space-y-4" data-testid={`redeem-card-${i}`}>
            <div className="flex items-center gap-2">
              <Image className="w-4 h-4 text-primary" />
              <Input value={r.name} onChange={(e) => patch(r.id, "name", e.target.value)} placeholder="Redeem name (e.g. Show my cat)" className="bg-secondary border-white/10 flex-1" data-testid={`redeem-name-${i}`} />
              <Button variant="outline" className="border-white/10" onClick={() => test(r.id)} data-testid={`redeem-test-${i}`}><Play className="w-4 h-4 mr-1" />Test</Button>
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-red-400" onClick={() => remove(r.id)} data-testid={`redeem-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>

            <div>
              <Label className="text-xs">Channel-point reward</Label>
              {r.reward_id ? (
                <div className="flex flex-wrap items-center gap-2 mt-1 rounded-md border border-white/10 bg-secondary px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 text-[11px] text-green-400" data-testid={`redeem-live-${i}`}><CheckCircle2 className="w-3.5 h-3.5" />Live on Twitch</span>
                  <span className="text-[11px] text-muted-foreground font-mono truncate max-w-[180px]" title={r.reward_id}>{r.reward_id}</span>
                  {bcStatus?.login && <a href={`https://dashboard.twitch.tv/u/${bcStatus.login}/viewer-rewards/channel-points/rewards`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline" data-testid={`redeem-seticon-${i}`}><ExternalLink className="w-3 h-3" />Set icon on Twitch</a>}
                  <div className="flex items-center gap-1.5 ml-auto"><Switch checked={r.is_enabled !== false} onCheckedChange={(v) => toggleReward(r, v)} data-testid={`redeem-enabled-${i}`} /><span className="text-[11px] text-muted-foreground">{r.is_enabled !== false ? "enabled" : "paused"}</span></div>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-2 mt-1">
                  <div className="w-32">
                    <div className="text-[10px] text-muted-foreground mb-1">Cost (points)</div>
                    <Input type="number" min={1} value={r.cost ?? 100} onChange={(e) => patch(r.id, "cost", parseInt(e.target.value) || 0)} className="bg-secondary border-white/10 h-9" data-testid={`redeem-cost-${i}`} />
                  </div>
                  <Button className="bg-primary hover:bg-primary/90 h-9" onClick={() => createReward(r)} disabled={creatingId === r.id || !bcStatus?.connected} data-testid={`redeem-create-${i}`}>
                    {creatingId === r.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
                    {creatingId === r.id ? "Creating…" : "Create on Twitch"}
                  </Button>
                  {!bcStatus?.connected && <span className="text-[11px] text-muted-foreground pb-2">Connect Twitch above first.</span>}
                </div>
              )}
              {!r.reward_id && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-[11px] text-muted-foreground">or paste an existing reward ID:</span>
                  <Input value={r.reward_id || ""} onChange={(e) => patch(r.id, "reward_id", e.target.value)} placeholder="reward ID" className="bg-secondary border-white/10 h-8 w-64 font-mono text-[11px]" data-testid={`redeem-manualid-${i}`} />
                  {lastRedeem && <button type="button" onClick={() => patch(r.id, "reward_id", lastRedeem)} className="text-[11px] text-primary hover:underline" data-testid={`redeem-uselast-${i}`}>use last redeemed</button>}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Image / GIF</Label>
                <div className="mt-1"><ImagePicker value={r.image_url} onChange={(v) => patch(r.id, "image_url", v)} testid={`redeem-image-${i}`} /></div>
              </div>
              <div>
                <Label className="text-xs">Sound</Label>
                <div className="mt-1"><SoundPicker value={r.sound_url} onChange={(v) => patch(r.id, "sound_url", v)} testid={`redeem-sound-${i}`} /></div>
              </div>
            </div>

            <div className="w-40">
              <Label className="text-xs">Display duration (seconds)</Label>
              <Input type="number" min={1} max={60} value={r.duration} onChange={(e) => patch(r.id, "duration", parseInt(e.target.value) || 1)} className="bg-secondary border-white/10 mt-1 h-9" data-testid={`redeem-duration-${i}`} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs">Position on screen</Label>
                <select value={r.position} onChange={(e) => patch(r.id, "position", e.target.value)} className="w-full bg-secondary border border-white/10 rounded-lg px-2 h-9 mt-1 text-sm focus:ring-primary focus:outline-none" data-testid={`redeem-position-${i}`}>
                  {POSITIONS.map((p) => <option key={p.v} value={p.v}>{p.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Size</Label>
                <select value={r.size} onChange={(e) => patch(r.id, "size", e.target.value)} className="w-full bg-secondary border border-white/10 rounded-lg px-2 h-9 mt-1 text-sm focus:ring-primary focus:outline-none" data-testid={`redeem-size-${i}`}>
                  {SIZES.map((sz) => <option key={sz.v} value={sz.v}>{sz.l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Cooldown (seconds)</Label>
                <Input type="number" min={0} max={3600} value={r.cooldown} onChange={(e) => patch(r.id, "cooldown", parseInt(e.target.value) || 0)} className="bg-secondary border-white/10 mt-1 h-9" data-testid={`redeem-cooldown-${i}`} />
              </div>
            </div>
          </div>
        ))}

        {items.length === 0 && (
          <div className="rounded-lg border border-dashed border-white/10 p-10 text-center text-sm text-muted-foreground" data-testid="redeems-empty">
            No media redeems yet. Add one to get started.
          </div>
        )}
      </div>

      <Button onClick={add} variant="outline" className="border-white/10 rounded-lg w-full" data-testid="add-redeem-button">
        <Plus className="w-4 h-4 mr-2" />Add media redeem
      </Button>
    </div>
  );
}
